import { useEffect } from "react";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function MentionsLegalesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO meta tags — noindex for legal page
    document.title = "Mentions Légales & Politique de Cookies | Securycles";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", "Mentions légales, politique de confidentialité et gestion des cookies du site Securycles, serrurier d'urgence à Créteil et dans le Val-de-Marne.");
    setMeta("robots", "noindex, nofollow");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/mentions-legales`);

    // Schema.org WebPage
    const schemaId = "schema-mentions-legales";
    let existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Mentions Légales — Securycles",
      "description": "Mentions légales, politique de confidentialité et gestion des cookies du site Securycles.",
      "url": `${SITE_URL}/mentions-legales`,
      "isPartOf": {
        "@type": "WebSite",
        "name": "Securycles",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "LocalBusiness",
        "name": "Securycles",
        "url": SITE_URL,
        "telephone": "+33604026840",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Créteil",
          "postalCode": "94000",
          "addressRegion": "Val-de-Marne",
          "addressCountry": "FR"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
      // Restore robots to index on unmount
      const robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (robotsMeta) robotsMeta.setAttribute("content", "index, follow");
    };
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAFA] pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 text-sm hover:text-black transition-colors cursor-pointer mb-6"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
          <div
            className="h-2 w-24 rounded-full mb-6"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, #FFB100 0px, #FFB100 10px, #000000 10px, #000000 20px)",
            }}
          ></div>
          <h1 className="text-3xl md:text-4xl font-black text-black uppercase mb-2">
            Mentions Légales
          </h1>
          <p className="text-gray-500 text-sm">Dernière mise à jour : 8 avril 2025</p>
        </div>

        <div className="space-y-10 text-gray-700">
          {/* Éditeur */}
          <section>
            <h4 id="editeur" className="text-lg font-black text-black uppercase mb-4 flex items-center gap-2">
              <a href="#editeur" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-building-line text-[#FFB100] mr-2"></i>
                Éditeur du site
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-2 text-sm">
              <p><strong>Raison sociale :</strong> Securycles</p>
              <p><strong>Activité :</strong> Serrurier d&apos;urgence — Créteil et Val-de-Marne (94)</p>
              <p><strong>Téléphone :</strong> <a href="tel:0604026840" className="text-[#FFB100] font-bold hover:underline">06 04 02 68 40</a></p>
              <p><strong>Zone d&apos;intervention :</strong> Créteil (94000) et tout le département du Val-de-Marne (94)</p>
              <p><strong>Horaires :</strong> 24h/24 — 7j/7</p>
            </div>
          </section>

          {/* Hébergement */}
          <section>
            <h4 id="hebergement" className="text-lg font-black text-black uppercase mb-4">
              <a href="#hebergement" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-server-line text-[#FFB100] mr-2"></i>
                Hébergement
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm space-y-2">
              <p><strong>Hébergeur :</strong> Readdy.ai</p>
              <p><strong>Site :</strong> <a href="https://readdy.ai" target="_blank" rel="nofollow noopener noreferrer" className="text-[#FFB100] hover:underline">https://readdy.ai</a></p>
            </div>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h4 id="propriete" className="text-lg font-black text-black uppercase mb-4">
              <a href="#propriete" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-copyright-line text-[#FFB100] mr-2"></i>
                Propriété intellectuelle
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm leading-relaxed">
              <p>
                L&apos;ensemble du contenu de ce site (textes, images, logos, graphismes) est la propriété exclusive de Securycles. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, est interdite sans autorisation préalable écrite.
              </p>
            </div>
          </section>

          {/* Données personnelles */}
          <section>
            <h4 id="donnees" className="text-lg font-black text-black uppercase mb-4">
              <a href="#donnees" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-shield-user-line text-[#FFB100] mr-2"></i>
                Données personnelles (RGPD)
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm leading-relaxed space-y-3">
              <p>
                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez d&apos;un droit d&apos;accès, de rectification, de suppression et de portabilité de vos données personnelles.
              </p>
              <p>
                Les données collectées via le formulaire de newsletter sont utilisées uniquement pour l&apos;envoi d&apos;informations relatives aux services de Securycles. Elles ne sont jamais cédées à des tiers.
              </p>
              <p>
                Pour exercer vos droits, contactez-nous par téléphone au <a href="tel:0604026840" className="text-[#FFB100] font-bold hover:underline">06 04 02 68 40</a> ou via WhatsApp.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section id="cookies">
            <h4 className="text-lg font-black text-black uppercase mb-4">
              <a href="#cookies" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-shield-check-line text-[#FFB100] mr-2"></i>
                Politique de cookies
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm leading-relaxed space-y-3">
              <p>
                Ce site utilise des cookies pour améliorer votre expérience de navigation. Vous pouvez à tout moment modifier vos préférences via la bannière de gestion des cookies.
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-check-line text-[#FFB100] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-black">Cookies essentiels</p>
                    <p className="text-gray-500">Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-bar-chart-line text-[#FFB100] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-black">Cookies analytiques</p>
                    <p className="text-gray-500">Mesure d&apos;audience anonymisée pour améliorer le site. Activables/désactivables.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <i className="ri-advertisement-line text-[#FFB100] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-black">Cookies marketing</p>
                    <p className="text-gray-500">Publicités personnalisées. Désactivés par défaut.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Responsabilité */}
          <section>
            <h4 id="responsabilite" className="text-lg font-black text-black uppercase mb-4">
              <a href="#responsabilite" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-error-warning-line text-[#FFB100] mr-2"></i>
                Limitation de responsabilité
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm leading-relaxed">
              <p>
                Securycles s&apos;efforce d&apos;assurer l&apos;exactitude et la mise à jour des informations diffusées sur ce site. Cependant, Securycles ne peut garantir l&apos;exactitude, la précision ou l&apos;exhaustivité des informations mises à disposition sur ce site.
              </p>
            </div>
          </section>

          {/* Droit applicable */}
          <section>
            <h4 id="droit" className="text-lg font-black text-black uppercase mb-4">
              <a href="#droit" className="hover:text-[#FFB100] transition-colors">
                <i className="ri-scales-line text-[#FFB100] mr-2"></i>
                Droit applicable
              </a>
            </h4>
            <div className="bg-white rounded-xl p-5 border border-gray-100 text-sm leading-relaxed">
              <p>
                Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.
              </p>
            </div>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 bg-[#FFB100] text-black font-bold text-sm px-6 py-3 rounded-full hover:bg-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-up-line"></i>
            Retour en haut
          </button>
        </div>
      </div>
    </main>
  );
}
